// src/design-system/components/richTextEditor/LinkModal.tsx
import { useState, useEffect, useMemo } from "react";
import { Link2 } from "lucide-react";
import * as yup from "yup";
import { Modal } from "../modal/modal";
import { Input } from "../input/Input";
import { Checkbox } from "../checkbox/Checkbox";
import { colors } from "../../theme/colors";
import type { LinkModalProps } from "./types";

// URL validation schema
const linkSchema = yup.object().shape({
  text: yup
    .string()
    .max(120, "Text must be 120 characters or less"),
  url: yup
    .string()
    .required("URL is required")
    .test("has-content", "URL is required", (value) => {
      return !!value?.trim();
    })
    .test("valid-url", "Please enter a valid URL", (value) => {
      if (!value?.trim()) return false;

      let urlToTest = value.trim();

      // Add protocol if missing
      if (!/^https?:\/\//i.test(urlToTest)) {
        urlToTest = `https://${urlToTest}`;
      }

      try {
        const url = new URL(urlToTest);
        // Must be http or https
        if (!["http:", "https:"].includes(url.protocol)) {
          return false;
        }
        // Must have a valid hostname
        if (!url.hostname || url.hostname.length < 1) {
          return false;
        }
        // Basic domain validation (must have at least one dot for TLD, unless localhost)
        if (url.hostname !== "localhost" && !url.hostname.includes(".")) {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    })
    .test("no-spaces", "URL cannot contain spaces", (value) => {
      if (!value) return true;
      return !value.includes(" ");
    })
    .test("valid-characters", "URL contains invalid characters", (value) => {
      if (!value?.trim()) return true;
      // Check for obviously invalid characters
      const invalidChars = /[<>{}|\\^`[\]]/;
      return !invalidChars.test(value);
    }),
});

// Helper to ensure URL has protocol
const ensureProtocol = (url: string): string => {
  const trimmedUrl = url.trim();
  if (!/^https?:\/\//i.test(trimmedUrl)) {
    return `https://${trimmedUrl}`;
  }
  return trimmedUrl;
};

export const LinkModal = ({
  open,
  onClose,
  onConfirm,
  initialUrl = "",
  initialText = "",
  initialNewTab = true,
  zIndex,
}: LinkModalProps) => {
  const [url, setUrl] = useState(initialUrl);
  const [text, setText] = useState(initialText);
  const [openInNewTab, setOpenInNewTab] = useState(initialNewTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [textTouched, setTextTouched] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setText(initialText);
      setOpenInNewTab(initialNewTab);
      setError(null);
      setTextError(null);
      setTouched(false);
      setTextTouched(false);
    }
  }, [open, initialUrl, initialText, initialNewTab]);

  // Validate URL and determine if form is valid
  const isValid = useMemo(() => {
    try {
      linkSchema.validateSync({ text, url });
      return true;
    } catch {
      return false;
    }
  }, [text, url]);

  // Validate on change and show error only if touched
  const validateUrl = (value: string) => {
    try {
      linkSchema.validateSync({ text, url: value });
      setError(null);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setError(err.path === "url" ? err.message : null);
      }
    }
  };

  const validateText = (value: string) => {
    try {
      linkSchema.validateSync({ text: value, url });
      setTextError(null);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setTextError(err.path === "text" ? err.message : null);
      }
    }
  };

  const handleTextChange = (value: string) => {
    setText(value);
    if (textTouched) {
      validateText(value);
    }
  };

  const handleTextBlur = () => {
    setTextTouched(true);
    validateText(text);
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
    if (touched) {
      validateUrl(value);
    }
  };

  const handleUrlBlur = () => {
    setTouched(true);
    validateUrl(url);
  };

  const handleOk = async () => {
    setTouched(true);

    try {
      linkSchema.validateSync({ text, url });
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setTextError(err.path === "text" ? err.message : null);
        setError(err.path === "url" ? err.message : null);
      }
      return;
    }

    const processedUrl = ensureProtocol(url);
    const processedText = text.trim();

    setLoading(true);
    try {
      onConfirm(processedUrl, processedText, openInNewTab);
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUrl("");
    setText("");
    setOpenInNewTab(true);
    setError(null);
    setTextError(null);
    setTouched(false);
    setTextTouched(false);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && isValid) {
      e.preventDefault();
      handleOk();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      getContainer={() => document.body}
      title={initialUrl ? "Edit Link" : "Insert Link"}
      icon={<Link2 size={20} />}
      size="medium"
      onOk={handleOk}
      onCancel={handleClose}
      okText={initialUrl ? "Update" : "Insert"}
      cancelText="Cancel"
      confirmLoading={loading}
      okButtonProps={{ disabled: !isValid }}
      zIndex={zIndex}
    >
      <div className="p-6 space-y-4">
        <Input
          label="Text"
          placeholder="Download, Apply now, Learn more"
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          onBlur={handleTextBlur}
          onKeyDown={handleKeyDown}
          error={textTouched && textError ? textError : undefined}
          autoFocus
          variant="filled"
        />

        <Input
          label="URL"
          required
          placeholder="https://example.com"
          value={url}
          onChange={(e) => handleUrlChange(e.target.value)}
          onBlur={handleUrlBlur}
          onKeyDown={handleKeyDown}
          error={touched && error ? error : undefined}
          prefix={<Link2 size={16} style={{ color: colors.textMuted }} />}
          variant="filled"
        />

        <Checkbox
          checked={openInNewTab}
          onChange={(e) => setOpenInNewTab(e.target.checked)}
          label="Open in new tab"
          size="medium"
        />
      </div>
    </Modal>
  );
};

export default LinkModal;
